'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { z } from 'zod';
import Image from 'next/image';
import styles from './Newsletter.module.css';

// Newsletter form schema for validation
const newsletterSchema = z.object({
  Email: z.string().email('Please enter a valid email address'),
  FullName: z.string().min(1, 'Full name is required'),
});

type NewsletterFormData = z.infer<typeof newsletterSchema>;

interface NewsletterProps {
  className?: string;
  locale: string;
}

export const Newsletter: React.FC<NewsletterProps> = ({ className, locale }) => {
  const t = useTranslations('newsletter');
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewsletterFormData>();

  const onSubmit = async (data: NewsletterFormData) => {
    try {
      setIsLoading(true);
      setSubscriptionStatus(null);
      setErrorMessage('');

      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 422) {
          throw new Error(t('userExists'));
        }
        throw new Error(result.message || 'Failed to subscribe');
      }

      setSubscriptionStatus('success');
      reset();
    } catch (error) {
      setSubscriptionStatus('error');
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const backgroundImage = locale === 'ar' 
    ? '/images/newsletter/arabic-Subscribe-Container.webp' 
    : '/images/newsletter/english-Subscribe-Container.webp';

  return (
    <section 
      className={`${styles.wrapper} ${className || ''}`} 
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
      aria-labelledby="newsletter-title"
    >
      <Image
        src={backgroundImage}
        alt=""
        className={styles.backgroundImage}
        fill
        priority
        sizes="(max-width: 480px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 100vw, 100vw"
        quality={90}
      />
      <div className={styles.content}>
        <div className={styles.textContent}>
          <h2 id="newsletter-title" className={styles.title}>
            {t('title')}
          </h2>
          <p className={styles.subtitle}>
            {t('subtitle')}
          </p>
          
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form} noValidate>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                id="fullName"
                aria-label={t('fullNamePlaceholder')}
                aria-invalid={errors.FullName ? 'true' : 'false'}
                {...register('FullName', {
                  required: t('fullNameRequired'),
                })}
                className={`${styles.input} ${errors.FullName ? styles.error : ''}`}
                placeholder={t('fullNamePlaceholder')}
                disabled={isLoading}
                autoComplete="name"
              />
              <input
                type="email"
                id="email"
                aria-label={t('emailPlaceholder')}
                aria-invalid={errors.Email ? 'true' : 'false'}
                {...register('Email', {
                  required: t('emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('invalidEmail'),
                  },
                })}
                className={`${styles.input} ${errors.Email ? styles.error : ''}`}
                placeholder={t('emailPlaceholder')}
                disabled={isLoading}
                autoComplete="email"
              />
              <button
                type="submit"
                disabled={isLoading}
                className={styles.button}
                aria-busy={isLoading}
              >
                {isLoading ? t('subscribing') : t('subscribe')}
              </button>
            </div>
            {errors.FullName && (
              <p className={styles.errorMessage} role="alert">{errors.FullName.message}</p>
            )}
            {errors.Email && (
              <p className={styles.errorMessage} role="alert">{errors.Email.message}</p>
            )}
          </form>

          {subscriptionStatus === 'success' && (
            <p className={styles.successMessage} role="status">
              {t('successMessage')}
            </p>
          )}

          {subscriptionStatus === 'error' && (
            <p className={styles.errorMessage} role="alert">
              {errorMessage || t('errorMessage')}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}; 